'use client';

import {
  FC,
  Fragment,
  useState,
  Children,
  ReactNode,
  CSSProperties,
  cloneElement,
  isValidElement,
} from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import WaveGridBackground from '@/components/WaveGridBackground';
import { RevealOnScroll } from '@/components/common/RevealOnScroll';
import { useCountUp } from '@/hooks';

/**
 * Props for `FeatureOverview2`.
 */
export type FeatureOverview2Props =
  SliceComponentProps<Content.FeatureOverview2Slice>;

// Per-card base gradients, indexed by position in the 3x2 grid (Figma node 17778:3091).
// Each card has a slightly different gradient angle/stops so the grid reads as
// six distinct facets rather than a flat tile.
const CARD_BASE_GRADIENTS = [
  'linear-gradient(-19.98deg, rgba(19,9,74,0.80) 26.5%, rgba(4,17,24,0.64) 48.5%)',
  'linear-gradient(179.18deg, rgba(4,17,24,0.64) 23.7%, rgba(19,9,74,0.80) 54.3%)',
  'linear-gradient(19.08deg, rgba(19,9,74,0.80) 30.1%, rgba(4,17,24,0.64) 47.3%)',
  'linear-gradient(196.02deg, rgba(19,9,74,0.80) 27.8%, rgba(4,17,24,0.64) 46.3%)',
  'linear-gradient(180deg, rgba(19,9,74,0.80) 42.4%, rgba(4,17,24,0.64) 84.9%)',
  'linear-gradient(156.93deg, rgba(19,9,74,0.80) 28.7%, rgba(4,17,24,0.64) 54.6%)',
];

const CARD_HIGHLIGHT_OVERLAY =
  'linear-gradient(90deg, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0.20) 100%)';

/**
 * Walks rich-text children and replaces `[highlight]...[/highlight]` and
 * `[highlight_b]...[/highlight_b]` markers in string leaves with colored
 * spans, preserving any nested inline formatting (e.g. <strong>, <em>).
 */
const HIGHLIGHT_REGEX =
  /(\[highlight\][^]*?\[\/highlight\]|\[highlight_b\][^]*?\[\/highlight_b\])/g;

const renderHighlightString = (text: string): ReactNode => {
  const parts = text.split(HIGHLIGHT_REGEX);
  if (parts.length === 1) {
    return text;
  }
  return parts.map((part, i) => {
    const green = part.match(/^\[highlight\]([^]*?)\[\/highlight\]$/);
    if (green) {
      return (
        <span key={i} className="font-medium text-[#00F557]">
          {green[1]}
        </span>
      );
    }
    const blue = part.match(/^\[highlight_b\]([^]*?)\[\/highlight_b\]$/);
    if (blue) {
      return (
        <span key={i} className="text-[#21007c]">
          {blue[1]}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

const withHighlights = (children: ReactNode): ReactNode => {
  return Children.map(children, (child) => {
    if (typeof child === 'string') {
      return renderHighlightString(child);
    }
    if (
      isValidElement<{ children?: ReactNode }>(child) &&
      child.props?.children !== undefined
    ) {
      return cloneElement(child, {
        children: withHighlights(child.props.children),
      });
    }
    return child;
  });
};

/**
 * Default grid variation.
 */
const DefaultGrid: FC<{
  slice: Extract<Content.FeatureOverview2Slice, { variation: 'default_grid' }>;
}> = ({ slice }) => {
  const {
    eyebrow,
    headline,
    description,
    features = [],
    background_image,
  } = slice.primary;
  const subheadline = (
    slice.primary as { subheadline?: import('@prismicio/client').RichTextField }
  ).subheadline;

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const isHighlighted = (i: number) =>
    hoveredIndex !== null ? hoveredIndex === i : i === 1;

  const splitRows = !!(slice.primary as { split_rows?: boolean }).split_rows;

  const renderCard = (feature: (typeof features)[number], i: number) => {
    const highlighted = isHighlighted(i);
    const baseGradient = CARD_BASE_GRADIENTS[i % CARD_BASE_GRADIENTS.length];
    const background = highlighted
      ? `${CARD_HIGHLIGHT_OVERLAY}, ${baseGradient}`
      : baseGradient;
    return (
      <div
        key={i}
        onMouseOver={() => setHoveredIndex(i)}
        onMouseLeave={() => setHoveredIndex(null)}
        className="flex min-h-[181px] cursor-default flex-col items-center justify-center gap-[4px] px-[16px] py-[28px] text-center transition-[background] duration-300 md:p-[36px]"
        style={{ background }}
      >
        {isFilled.image(feature.icon) && (
          <PrismicNextImage
            field={feature.icon}
            className="size-[51px] object-contain"
            fallbackAlt=""
          />
        )}
        {feature.title && (
          <p className="text-balance text-[14px] font-light leading-[1.5] text-white lg:text-[18px]">
            {feature.title}
          </p>
        )}
      </div>
    );
  };

  const V_FADE =
    'linear-gradient(to bottom, transparent 0%, rgba(168,135,255,0.35) 25%, rgba(168,135,255,0.35) 75%, transparent 100%)';
  const H_FADE =
    'linear-gradient(to right, transparent 0%, rgba(168,135,255,0.35) 25%, rgba(168,135,255,0.35) 75%, transparent 100%)';

  const renderSplitRow = (row: (typeof features)[number][], offset: number) => (
    <div
      className="relative grid grid-cols-2 md:[grid-template-columns:repeat(var(--cols),minmax(0,1fr))]"
      style={{ '--cols': row.length } as CSSProperties}
    >
      {row.map((feature, i) => renderCard(feature, offset + i))}
      {Array.from({ length: Math.max(row.length - 1, 0) }, (_, i) => (
        <div
          key={`v-${i}`}
          className="pointer-events-none absolute inset-y-0 hidden w-px md:block"
          style={{
            left: `calc(100% / ${row.length} * ${i + 1})`,
            background: V_FADE,
          }}
        />
      ))}
    </div>
  );

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative mx-5 overflow-hidden rounded-[24px] bg-[#041118] px-[24px] py-[60px] md:mx-[30px] md:px-[60px]"
    >
      {isFilled.image(background_image) && (
        <PrismicNextImage
          field={background_image}
          fill
          className="pointer-events-none absolute inset-0 size-full object-cover opacity-60"
          alt=""
        />
      )}

      <div className="relative z-10 mx-auto flex max-w-[1080px] flex-col items-center gap-[60px]">
        <div className="flex flex-col items-center gap-[24px] text-center">
          {eyebrow && (
            <span className="inline-block rounded-full border border-[#00f557] px-[16px] py-[8px] text-[14px] font-normal uppercase leading-[1.2] text-white">
              {eyebrow}
            </span>
          )}

          {(isFilled.richText(headline) || isFilled.richText(subheadline)) && (
            <div className="flex flex-col items-center">
              {isFilled.richText(headline) && (
                <PrismicRichText
                  field={headline}
                  components={{
                    heading2: ({ children }) => (
                      <h2 className="text-[32px] font-semibold leading-[1.2] text-white lg:text-[42px]">
                        {children}
                      </h2>
                    ),
                  }}
                />
              )}
              {isFilled.richText(subheadline) && (
                <PrismicRichText
                  field={subheadline}
                  components={{
                    heading2: ({ children }) => (
                      <h2 className="text-[32px] font-semibold leading-[1.2] text-white lg:text-[42px]">
                        {children}
                      </h2>
                    ),
                  }}
                />
              )}
            </div>
          )}

          {isFilled.richText(description) && (
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="max-w-[1080px] text-[16px] font-medium leading-[1.4] text-[#00f557] lg:text-[20px]">
                    {children}
                  </p>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold">{children}</strong>
                ),
              }}
            />
          )}
        </div>

        {features.length > 0 &&
          (splitRows ? (
            <div className="relative mx-auto w-full max-w-[1080px] overflow-hidden rounded-[16px]">
              {/* Phone: a clean 2-up grid of every card. The split-into-halves
                  layout below would otherwise orphan the last card of each
                  odd-sized half on its own row. */}
              <div className="grid auto-rows-fr grid-cols-2 md:hidden">
                {features.map((feature, i) => renderCard(feature, i))}
              </div>

              {/* md+: the designed split rows, each sized to its own column
                  count, with a fading divider between them. */}
              <div className="hidden md:block">
                {renderSplitRow(
                  features.slice(0, Math.floor(features.length / 2)),
                  0,
                )}
                <div
                  className="pointer-events-none h-px w-full"
                  style={{ background: H_FADE }}
                />
                {renderSplitRow(
                  features.slice(Math.floor(features.length / 2)),
                  Math.floor(features.length / 2),
                )}
              </div>
            </div>
          ) : (
            <div className="relative mx-auto w-full max-w-[1080px] overflow-hidden rounded-[16px]">
              <div className="grid auto-rows-fr grid-cols-2 md:grid-cols-3">
                {features.map((feature, i) => renderCard(feature, i))}
              </div>

              {/* Subtle fading divider lines — soft enough to feel like part
                  of the component rather than visible borders. */}
              {/* Vertical dividers between the 3 columns (md+). */}
              <div
                className="pointer-events-none absolute inset-y-0 hidden w-px md:block"
                style={{ left: 'calc(100% / 3)', background: V_FADE }}
              />
              <div
                className="pointer-events-none absolute inset-y-0 hidden w-px md:block"
                style={{ left: 'calc(100% / 3 * 2)', background: V_FADE }}
              />
              {/* Horizontal dividers at every real row boundary (md+), derived
                  from the item count, so they always line up with the cards and
                  the hover — instead of a hard-coded 50% that only matched a
                  2-row grid. */}
              {Array.from(
                { length: Math.max(Math.ceil(features.length / 3) - 1, 0) },
                (_, r) => (
                  <div
                    key={`hdiv-${r}`}
                    className="pointer-events-none absolute inset-x-0 hidden h-px md:block"
                    style={{
                      top: `calc(100% / ${Math.ceil(features.length / 3)} * ${r + 1})`,
                      background: H_FADE,
                    }}
                  />
                ),
              )}
            </div>
          ))}
      </div>
    </section>
  );
};

/**
 * Table variation — comparison of "Unmanaged AI" (left plain text column)
 * vs. a highlighted dark card (right) with matching rows per Figma
 * node 17724:1520.
 */
const TableVersion: FC<{
  slice: Extract<Content.FeatureOverview2Slice, { variation: 'tableVersion' }>;
}> = ({ slice }) => {
  const {
    eyebrow,
    headline,
    description,
    background_image,
    table_2_image,
    table_1_header,
    table_2_header,
    table_row = [],
  } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative mx-[30px] overflow-hidden rounded-[24px] bg-[#041118] px-[24px] py-[60px] md:px-[60px] lg:mx-[60px]"
    >
      {isFilled.image(background_image) && (
        <PrismicNextImage
          field={background_image}
          fill
          className="pointer-events-none absolute inset-0 size-full object-cover opacity-40"
          alt=""
        />
      )}

      <div className="relative z-10 mx-auto flex w-full max-w-[1080px] flex-col items-center gap-[60px]">
        {/* Header */}
        <div className="flex w-full flex-col items-center gap-[24px] text-center">
          {eyebrow && (
            <span className="inline-block rounded-full border border-[#00f557] px-[16px] py-[8px] text-[14px] font-normal uppercase leading-[1.2] text-white">
              {eyebrow}
            </span>
          )}

          {isFilled.richText(headline) && (
            <PrismicRichText
              field={headline}
              components={{
                heading2: ({ children }) => (
                  <h2 className="w-full text-[32px] font-semibold capitalize leading-[1.2] text-white lg:text-[42px]">
                    {withHighlights(children)}
                  </h2>
                ),
              }}
            />
          )}

          {isFilled.richText(description) && (
            <div className="w-full text-[16px] font-light leading-[1.4] text-white">
              <PrismicRichText
                field={description}
                components={{
                  paragraph: ({ children }) => (
                    <p className="mb-0 leading-[1.4]">
                      {withHighlights(children)}
                    </p>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-medium">{children}</strong>
                  ),
                }}
              />
            </div>
          )}
        </div>

        {/* Two-column comparison */}
        {table_row.length > 0 && (
          <div className="flex w-full flex-col gap-[24px] md:flex-row md:items-start">
            {/* Left column — plain */}
            <div className="flex w-full shrink-0 flex-col gap-[24px] py-[24px] md:w-[318px]">
              {table_1_header && (
                <div className="flex h-[60px] items-center py-[16px]">
                  <p className="text-[20px] font-semibold leading-[1.4] text-white">
                    {table_1_header}
                  </p>
                </div>
              )}
              <div className="flex flex-col">
                {table_row.map((row, i) => (
                  <div
                    key={`table-1-row-${i}`}
                    className={`flex h-[100px] items-center py-[12px] ${
                      i < table_row.length - 1
                        ? 'border-b-[0.5px] border-[#BABABA]'
                        : ''
                    }`}
                  >
                    {row.table_1_text && (
                      <p className="text-[18px] font-normal leading-[1.5] text-white">
                        {row.table_1_text}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Right column — highlighted card */}
            <div className="relative min-w-0 flex-1 overflow-hidden rounded-[20px] border border-white/50 p-[24px]">
              {isFilled.image(table_2_image) && (
                <>
                  <PrismicNextImage
                    field={table_2_image}
                    fill
                    className="pointer-events-none absolute inset-0 scale-110 object-cover"
                    style={{ filter: 'blur(4px)' }}
                    alt=""
                  />
                  {/* subtle dark tint over entire card */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 rounded-[20px] bg-black/20"
                  />
                  {/* heavier dark overlay covering rows area, leaving header lighter */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute bottom-0 left-0 right-0 bg-black/60"
                    style={{ top: '101px' }}
                  />
                </>
              )}

              <div className="relative flex flex-col gap-[24px]">
                {table_2_header && (
                  <div className="flex items-center py-[16px]">
                    <p className="text-[20px] font-semibold leading-[1.4] text-[#00F557]">
                      {table_2_header}
                    </p>
                  </div>
                )}
                <div className="flex flex-col">
                  {table_row.map((row, i) => (
                    <div
                      key={`table-2-row-${i}`}
                      className={`flex h-[100px] items-center py-[12px] ${
                        i < table_row.length - 1
                          ? 'border-b-[0.5px] border-[#444]'
                          : ''
                      }`}
                    >
                      {row.table_2_text && (
                        <p className="text-[18px] font-normal leading-[1.5] text-white">
                          {row.table_2_text}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

/**
 * Path Forward variation — dark themed 3x3 grid with one optionally
 * highlighted (green) outcome card.
 */
const PathForward: FC<{
  slice: Extract<Content.FeatureOverview2Slice, { variation: 'pathForward' }>;
}> = ({ slice }) => {
  const { eyebrow, headline, description, features = [] } = slice.primary;
  const highlight = (slice.primary as { highlight?: string }).highlight;
  const chips = (slice.primary as { chips?: { label?: string }[] }).chips ?? [];

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const isActive = (i: number) =>
    hoveredIndex !== null ? hoveredIndex === i : i === 1;

  const cols = 3;
  const rows = Math.ceil(features.length / cols);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-white px-[30px] py-12 md:py-16"
    >
      <div
        className="w-full overflow-hidden rounded-[24px] px-6 py-12 md:px-10 md:py-14"
        style={{
          background:
            'radial-gradient(55% 40% at 50% 45%, rgba(75,15,234,0.22) 0%, rgba(33,0,124,0.08) 55%, rgba(1,6,10,0) 100%), #01060a',
        }}
      >
        <div className="flex flex-col items-center gap-5 text-center">
          {eyebrow && (
            <span className="inline-block rounded-full border border-[#00f557] px-5 py-2 text-[12px] font-normal uppercase leading-[1.2] tracking-[0.12em] text-white md:text-[13px]">
              {eyebrow}
            </span>
          )}

          {isFilled.richText(headline) && (
            <PrismicRichText
              field={headline}
              components={{
                heading2: ({ children }) => (
                  <h2 className="max-w-[1040px] text-[32px] font-bold leading-[1.15] text-white md:text-[44px]">
                    {children}
                  </h2>
                ),
              }}
            />
          )}

          {isFilled.richText(description) && (
            <div className="w-full">
              <PrismicRichText
                field={description}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-[14px] leading-[1.5] text-white/75 md:whitespace-nowrap md:text-[15px]">
                      {children}
                    </p>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold">{children}</strong>
                  ),
                }}
              />
            </div>
          )}

          {highlight && (
            <p className="mt-[8px] text-[16px] font-semibold leading-[1.4] text-[#00f557] md:text-[18px]">
              {highlight}
            </p>
          )}

          {chips.length > 0 && (
            <div className="flex max-w-[920px] flex-wrap items-center justify-center gap-[10px]">
              {chips.map((chip, i) =>
                chip.label ? (
                  <span
                    key={i}
                    className="inline-block rounded-full border border-white/15 bg-white/[0.03] px-[16px] py-[8px] text-[13px] font-normal leading-[1.2] text-white/65"
                  >
                    {chip.label}
                  </span>
                ) : null,
              )}
            </div>
          )}
        </div>

        {features.length > 0 && (
          <div className="relative mx-auto mt-10 w-full max-w-[1080px] overflow-hidden rounded-[16px] md:mt-[40px]">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {features.map((feature, i) => {
                const highlighted = feature.is_highlighted;
                const baseGradient =
                  CARD_BASE_GRADIENTS[i % CARD_BASE_GRADIENTS.length];
                const active = !highlighted && isActive(i);

                const cardCls = highlighted
                  ? 'relative flex min-h-[260px] flex-col items-center gap-[10px] rounded-[8px] px-[24px] py-[32px] text-center'
                  : 'relative flex min-h-[260px] cursor-default flex-col items-center gap-[10px] px-[24px] py-[32px] text-center transition-[background] duration-300';

                let cardStyle: CSSProperties | undefined;
                if (highlighted) {
                  cardStyle = {
                    background:
                      'linear-gradient(180deg, rgba(0,245,87,0.06) 0%, rgba(0,245,87,0.015) 100%)',
                    boxShadow:
                      '0 0 0 1.5px #00f557, 0 0 16px rgba(0,245,87,0.25), inset 0 0 28px rgba(0,245,87,0.05)',
                  };
                } else {
                  const background = active
                    ? `${CARD_HIGHLIGHT_OVERLAY}, ${baseGradient}`
                    : baseGradient;
                  cardStyle = {
                    background,
                    backdropFilter: 'blur(5.4px)',
                    WebkitBackdropFilter: 'blur(5.4px)',
                  };
                }

                return (
                  <div
                    key={i}
                    className={cardCls}
                    style={cardStyle}
                    onMouseOver={() => setHoveredIndex(i)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    {isFilled.image(feature.icon) && (
                      <div className="h-[44px] w-[44px] shrink-0">
                        <PrismicNextImage
                          field={feature.icon}
                          className="h-full w-full object-contain"
                          fallbackAlt=""
                        />
                      </div>
                    )}

                    {feature.title && (
                      <h3
                        className={`max-w-[260px] text-[18px] font-bold leading-[1.3] md:text-[20px] ${
                          highlighted ? 'text-[#00f557]' : 'text-white'
                        }`}
                      >
                        {feature.title}
                      </h3>
                    )}

                    {feature.description && (
                      <p
                        className={`max-w-[260px] text-[13px] leading-[1.5] md:text-[14px] ${
                          highlighted ? 'text-white/85' : 'text-white/65'
                        }`}
                      >
                        {feature.description}
                      </p>
                    )}

                    {highlighted && feature.highlight_text && (
                      <p className="max-w-[260px] text-[13px] font-bold leading-[1.5] text-[#00f557] md:text-[14px]">
                        {feature.highlight_text}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Fading purple grid lines — drawn above the card gradients to
                read as the cards' bleeding outlines. */}
            {Array.from({ length: cols - 1 }, (_, i) => (
              <div
                key={`v-${i}`}
                className="pointer-events-none absolute inset-y-0 hidden w-px lg:block"
                style={{
                  left: `calc(100% / ${cols} * ${i + 1})`,
                  background:
                    'linear-gradient(to bottom, transparent 0%, #a887ff 15%, #a887ff 85%, transparent 100%)',
                }}
              />
            ))}
            {Array.from({ length: rows - 1 }, (_, i) => (
              <div
                key={`h-${i}`}
                className="pointer-events-none absolute inset-x-0 hidden h-px lg:block"
                style={{
                  top: `calc(100% / ${rows} * ${i + 1})`,
                  background:
                    'linear-gradient(to right, transparent 0%, #a887ff 15%, #a887ff 85%, transparent 100%)',
                }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

/**
 * Pilot Build variation — light themed grid with eyebrow, headline,
 * description, optional chips row, 3-col card grid (center highlighted),
 * and outcome footer.
 */
type PilotBuildSlice = {
  slice_type: string;
  variation: string;
  primary: {
    eyebrow?: string;
    headline?: import('@prismicio/client').RichTextField;
    description?: import('@prismicio/client').RichTextField;
    chips_heading?: string;
    chips?: { label?: string }[];
    features?: {
      icon?: import('@prismicio/client').ImageField;
      title?: string;
      description?: string;
      is_highlighted?: boolean;
    }[];
    outcome_label?: string;
    outcome_text?: string;
  };
};

const PilotBuild: FC<{ slice: PilotBuildSlice }> = ({ slice }) => {
  const {
    eyebrow,
    headline,
    description,
    chips_heading,
    chips = [],
    features = [],
    outcome_label,
    outcome_text,
  } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative overflow-hidden bg-white px-4 py-16 md:px-12 md:py-24"
    >
      <WaveGridBackground />

      <div className="relative z-[1] mx-auto flex w-full max-w-[1080px] flex-col items-center gap-[24px] text-center">
        {eyebrow && (
          <span className="inline-block rounded-full border border-[#4b0fea] px-[18px] py-[8px] text-[12px] font-normal uppercase leading-[1.2] tracking-[0.12em] text-[#041118] md:text-[13px]">
            {eyebrow}
          </span>
        )}

        {isFilled.richText(headline) && (
          <PrismicRichText
            field={headline}
            components={{
              heading2: ({ children }) => (
                <h2 className="max-w-[820px] text-[32px] font-bold leading-[1.15] text-[#041118] md:text-[44px]">
                  {children}
                </h2>
              ),
            }}
          />
        )}

        {isFilled.richText(description) && (
          <div className="max-w-[820px]">
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-[15px] leading-[1.5] text-[#041118]/75 md:text-[16px]">
                    {children}
                  </p>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold">{children}</strong>
                ),
              }}
            />
          </div>
        )}

        {chips_heading && (
          <p className="mt-[8px] text-[15px] font-semibold leading-[1.5] text-[#4b0fea] md:text-[16px]">
            {chips_heading}
          </p>
        )}

        {chips.length > 0 && (
          <div className="flex max-w-[920px] flex-wrap items-center justify-center gap-[12px]">
            {chips.map((chip, i) =>
              chip.label ? (
                <span
                  key={i}
                  className="inline-block rounded-full border border-[#d6d6e0] bg-white px-[16px] py-[8px] text-[14px] font-normal leading-[1.2] text-[#041118]"
                >
                  {chip.label}
                </span>
              ) : null,
            )}
          </div>
        )}
      </div>

      {features.length > 0 &&
        (() => {
          const cols = 3;
          const rows = Math.ceil(features.length / cols);
          // Build radial-gradient layers — one per interior grid intersection.
          // Each glow is sized smaller than a column so adjacent glows don't
          // bleed into each other, keeping them visually distinct per Figma.
          const glowLayers: string[] = [];
          for (let r = 1; r < rows; r++) {
            for (let c = 1; c < cols; c++) {
              const x = (c / cols) * 100;
              const y = (r / rows) * 100;
              glowLayers.push(
                `radial-gradient(ellipse 280px 180px at ${x}% ${y}%, rgba(0,245,87,0.22) 0%, rgba(0,245,87,0.10) 30%, rgba(0,245,87,0) 65%)`,
              );
            }
          }
          const gridBg =
            glowLayers.length > 0
              ? `${glowLayers.join(', ')}, #ffffff`
              : '#ffffff';

          return (
            <div className="relative z-[1] mx-auto mt-[40px] w-full max-w-[1080px] overflow-hidden rounded-[24px] border border-[#e6e6ed]">
              <div
                className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                style={{ background: gridBg }}
              >
                {features.map((feature, i) => {
                  const row = Math.floor(i / cols);
                  const col = i % cols;
                  const isLastCol = col === cols - 1;
                  const isLastRow = row === rows - 1;

                  const borders = `${!isLastCol ? 'lg:border-r' : ''} ${!isLastRow ? 'border-b' : ''} border-[#e6e6ed]`;

                  return (
                    <div
                      key={i}
                      className={`relative flex flex-col items-center gap-[10px] px-[28px] py-[40px] text-center ${borders}`}
                    >
                      {isFilled.image(feature.icon) && (
                        <div className="h-[44px] w-[44px] shrink-0">
                          <PrismicNextImage
                            field={feature.icon}
                            className="h-full w-full object-contain"
                            fallbackAlt=""
                          />
                        </div>
                      )}

                      {feature.title && (
                        <h3 className="max-w-[260px] text-[18px] font-bold leading-[1.3] text-[#041118] md:text-[20px]">
                          {feature.title}
                        </h3>
                      )}

                      {feature.description && (
                        <p className="max-w-[260px] text-[13px] font-light leading-[1.5] text-[#041118]/75 md:text-[14px]">
                          {feature.description}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

      {(outcome_label || outcome_text) && (
        <div className="relative z-[1] mx-auto mt-[32px] w-full max-w-[1080px] text-center">
          <p className="text-[15px] leading-[1.5] text-[#041118] md:text-[16px]">
            {outcome_label && (
              <span className="font-bold text-[#4b0fea]">{outcome_label} </span>
            )}
            {outcome_text && <span>{outcome_text}</span>}
          </p>
        </div>
      )}
    </section>
  );
};

/**
 * Complexity Cost variation — light themed callout with eyebrow pill,
 * two captioned 5-card text rows, and a Core Problem footer
 * (description + purple emphasis). Optional background image.
 */
type ComplexityCostSlice = {
  slice_type: string;
  variation: string;
  primary: {
    eyebrow?: string;
    top_caption?: string;
    top_items?: { label?: string }[];
    bottom_caption?: string;
    bottom_items?: { label?: string }[];
    footer_title?: string;
    footer_description?: string;
    footer_emphasis?: string;
    background_image?: import('@prismicio/client').ImageField;
  };
};

const ComplexityCost: FC<{ slice: ComplexityCostSlice }> = ({ slice }) => {
  const {
    eyebrow,
    top_caption,
    top_items = [],
    bottom_caption,
    bottom_items = [],
    footer_title,
    footer_description,
    footer_emphasis,
    background_image,
  } = slice.primary;

  const renderRow = (items: { label?: string }[], keyPrefix: string) => {
    if (items.length === 0) {
      return null;
    }
    return (
      <div className="relative grid w-full grid-cols-2 gap-y-6 sm:grid-cols-3 md:grid-cols-5">
        {items.map((item, i) => (
          <div
            key={`${keyPrefix}-${i}`}
            className={`flex items-start justify-center px-2 py-3 text-center ${
              i > 0 ? 'md:border-l md:border-[#041118]/15' : ''
            }`}
          >
            {item.label && (
              <p className="max-w-[130px] text-[15px] font-bold leading-[1.3] text-[#041118] md:text-[16px]">
                {item.label}
              </p>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative overflow-hidden bg-white px-4 py-16 md:px-12 md:py-24"
    >
      {isFilled.image(background_image) && (
        <PrismicNextImage
          field={background_image}
          fill
          className="pointer-events-none absolute inset-0 size-full object-cover opacity-60"
          alt=""
        />
      )}

      <div className="relative z-[1] mx-auto flex w-full max-w-[1180px] flex-col items-center gap-[40px]">
        {eyebrow && (
          <span className="inline-block rounded-full border border-[#4b0fea] px-[18px] py-[8px] text-[12px] font-normal uppercase leading-[1.2] tracking-[0.12em] text-[#041118] md:text-[13px]">
            {eyebrow}
          </span>
        )}

        {(top_caption || top_items.length > 0) && (
          <div className="flex w-full flex-col items-center gap-[28px]">
            {top_caption && (
              <p className="max-w-[820px] text-center text-[16px] font-normal leading-[1.5] text-[#041118] md:text-[18px]">
                {top_caption}
              </p>
            )}
            {renderRow(top_items, 'top')}
          </div>
        )}

        {(bottom_caption || bottom_items.length > 0) && (
          <div className="flex w-full flex-col items-center gap-[28px]">
            {bottom_caption && (
              <p className="max-w-[820px] text-center text-[16px] font-normal leading-[1.5] text-[#041118] md:text-[18px]">
                {bottom_caption}
              </p>
            )}
            {renderRow(bottom_items, 'bottom')}
          </div>
        )}

        {(footer_title || footer_description || footer_emphasis) && (
          <div className="mt-[16px] flex max-w-[820px] flex-col items-center gap-[8px] text-center">
            {footer_title && (
              <p className="text-[16px] font-bold leading-[1.4] text-[#041118] md:text-[18px]">
                {footer_title}
              </p>
            )}
            {footer_description && (
              <p className="text-[14px] font-normal leading-[1.5] text-[#041118] md:text-[15px]">
                {footer_description}
              </p>
            )}
            {footer_emphasis && (
              <p className="text-[16px] font-bold leading-[1.4] text-[#4b0fea] md:text-[18px]">
                {footer_emphasis}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

/**
 * Structured Delivery variation — dark themed: eyebrow + headline + 2x2
 * feature grid (icon + title + subtitle, one optionally highlighted by
 * default and on hover), caption, and 3-column comparison list (middle
 * column rendered as a dark glow card). Optional background image.
 */
type StructuredDeliverySlice = {
  slice_type: string;
  variation: string;
  primary: {
    eyebrow?: string;
    headline?: import('@prismicio/client').RichTextField;
    top_features?: {
      icon?: import('@prismicio/client').ImageField;
      title?: string;
      subtitle?: string;
      is_highlighted?: boolean;
    }[];
    caption?: string;
    column_1_title?: string;
    column_1_items?: { text?: string }[];
    column_2_title?: string;
    column_2_items?: { text?: string }[];
    column_3_title?: string;
    column_3_items?: { text?: string }[];
    column_2_background?: import('@prismicio/client').ImageField;
    primary_cta?: import('@prismicio/client').LinkField;
    secondary_cta?: import('@prismicio/client').LinkField;
    background_image?: import('@prismicio/client').ImageField;
  };
};

const StructuredDelivery: FC<{ slice: StructuredDeliverySlice }> = ({
  slice,
}) => {
  const {
    eyebrow,
    headline,
    top_features = [],
    caption,
    column_1_title,
    column_1_items = [],
    column_2_title,
    column_2_items = [],
    column_3_title,
    column_3_items = [],
    column_2_background,
    primary_cta,
    secondary_cta,
    background_image,
  } = slice.primary;

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const defaultHighlighted = top_features.findIndex((f) => f.is_highlighted);
  const defaultIndex = defaultHighlighted >= 0 ? defaultHighlighted : 1;
  const isActive = (i: number) =>
    hoveredIndex !== null ? hoveredIndex === i : i === defaultIndex;

  const renderColumn = (
    title: string | undefined,
    items: { text?: string }[],
    keyPrefix: string,
    titleColor: string,
  ) => (
    <div className="flex flex-col">
      {title && (
        <p
          className={`pb-[20px] text-[16px] font-bold leading-[1.4] md:text-[18px] ${titleColor}`}
        >
          {title}
        </p>
      )}
      <div className="flex flex-col">
        {items.map((item, i) =>
          item.text ? (
            <div
              key={`${keyPrefix}-${i}`}
              className={`py-[18px] ${
                i < items.length - 1 ? 'border-b border-white/10' : ''
              }`}
            >
              <p className="text-[14px] font-normal leading-[1.5] text-white md:text-[15px]">
                {item.text}
              </p>
            </div>
          ) : null,
        )}
      </div>
    </div>
  );

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative mx-4 overflow-hidden rounded-[24px] bg-[#01060a] px-4 py-16 sm:mx-[30px] md:px-12 md:py-24"
    >
      {isFilled.image(background_image) && (
        <PrismicNextImage
          field={background_image}
          fill
          className="pointer-events-none absolute inset-0 size-full object-cover opacity-50"
          alt=""
        />
      )}

      <div className="relative z-[1] mx-auto flex w-full max-w-[1140px] flex-col items-center gap-[40px]">
        <RevealOnScroll className="flex flex-col items-center gap-[16px] text-center">
          {eyebrow && (
            <span className="inline-block rounded-full border border-[#00f557] px-[16px] py-[8px] text-[14px] font-normal uppercase leading-[1.2] text-white">
              {eyebrow}
            </span>
          )}

          {isFilled.richText(headline) && (
            <PrismicRichText
              field={headline}
              components={{
                heading2: ({ children }) => (
                  <h2 className="text-[32px] font-semibold leading-[1.2] text-white lg:text-[42px]">
                    {children}
                  </h2>
                ),
              }}
            />
          )}
        </RevealOnScroll>

        {top_features.length > 0 && (
          <RevealOnScroll
            delay={150}
            className="relative w-full overflow-hidden rounded-[16px]"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2">
              {top_features.map((feature, i) => {
                const baseGradient =
                  CARD_BASE_GRADIENTS[i % CARD_BASE_GRADIENTS.length];
                const active = isActive(i);
                // Active state: lighter gray-purple fill (overrides the base
                // gradient with a wash of #a887ff at low alpha) plus a brighter
                // outline glow.
                const background = active
                  ? `linear-gradient(180deg, rgba(168,135,255,0.18) 0%, rgba(168,135,255,0.10) 100%), ${baseGradient}`
                  : baseGradient;
                const activeBoxShadow = active
                  ? 'inset 0 0 0 1px rgba(168,135,255,0.55), 0 0 24px -4px rgba(168,135,255,0.35)'
                  : undefined;

                return (
                  <div
                    key={i}
                    onMouseOver={() => setHoveredIndex(i)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    className="flex min-h-[220px] cursor-default flex-col items-center justify-center gap-[8px] p-[36px] text-center transition-[background,box-shadow] duration-300"
                    style={{
                      background,
                      boxShadow: activeBoxShadow,
                      backdropFilter: 'blur(5.4px)',
                      WebkitBackdropFilter: 'blur(5.4px)',
                    }}
                  >
                    {isFilled.image(feature.icon) && (
                      <PrismicNextImage
                        field={feature.icon}
                        className="mb-[8px] size-[48px] object-contain"
                        fallbackAlt=""
                      />
                    )}
                    {feature.title && (
                      <p className="text-[18px] font-bold leading-[1.3] text-white md:text-[20px]">
                        {feature.title}
                      </p>
                    )}
                    {feature.subtitle && (
                      <p className="text-[13px] font-normal leading-[1.5] text-white/80 md:text-[14px]">
                        {feature.subtitle}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Fading purple grid lines — vertical at 50%, horizontal at 50% */}
            <div
              className="pointer-events-none absolute inset-y-0 hidden w-px sm:block"
              style={{
                left: '50%',
                background:
                  'linear-gradient(to bottom, transparent 0%, #a887ff 15%, #a887ff 85%, transparent 100%)',
              }}
            />
            {top_features.length > 2 && (
              <div
                className="pointer-events-none absolute inset-x-0 hidden h-px sm:block"
                style={{
                  top: '50%',
                  background:
                    'linear-gradient(to right, transparent 0%, #a887ff 15%, #a887ff 85%, transparent 100%)',
                }}
              />
            )}
          </RevealOnScroll>
        )}

        {caption && (
          <p className="w-full text-balance text-center text-[14px] font-normal leading-[1.5] text-white/85 lg:whitespace-nowrap lg:text-[16px]">
            {caption}
          </p>
        )}

        {(column_1_items.length > 0 ||
          column_2_items.length > 0 ||
          column_3_items.length > 0) && (
          <RevealOnScroll
            delay={200}
            className="grid w-full grid-cols-1 gap-6 md:grid-cols-3 md:gap-0 md:items-start"
          >
            <div className="md:px-6">
              {renderColumn(
                column_1_title,
                column_1_items,
                'col1',
                'text-white',
              )}
            </div>

            <div
              className="relative overflow-hidden rounded-[20px] border border-white/15 p-6 md:-mt-3"
              style={{
                background:
                  'radial-gradient(120% 80% at 50% 0%, rgba(75,15,234,0.35) 0%, rgba(19,9,74,0.55) 45%, rgba(4,17,24,0.85) 100%)',
                boxShadow:
                  '0 12px 40px -8px rgba(75,15,234,0.45), 0 0 80px -20px rgba(168,135,255,0.4), inset 0 1px 0 0 rgba(255,255,255,0.08)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
              }}
            >
              {/* Soft purple radial glow behind the card */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 -z-10"
                style={{
                  background:
                    'radial-gradient(60% 50% at 50% 35%, rgba(168,135,255,0.25) 0%, rgba(168,135,255,0) 70%)',
                }}
              />
              {isFilled.image(column_2_background) && (
                <>
                  <PrismicNextImage
                    field={column_2_background}
                    fill
                    className="pointer-events-none absolute inset-0 -z-10 size-full object-cover opacity-60"
                    alt=""
                  />
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 -z-10 rounded-[20px] bg-gradient-to-b from-black/30 to-[#13094A]/60"
                  />
                </>
              )}
              <div className="relative">
                {renderColumn(
                  column_2_title,
                  column_2_items,
                  'col2',
                  'text-[#00f557]',
                )}
              </div>
            </div>

            <div className="md:px-6">
              {renderColumn(
                column_3_title,
                column_3_items,
                'col3',
                'text-white',
              )}
            </div>
          </RevealOnScroll>
        )}

        {(isFilled.link(primary_cta) || isFilled.link(secondary_cta)) && (
          <RevealOnScroll
            delay={350}
            className="flex flex-col items-stretch justify-center gap-3 pt-2 sm:flex-row sm:items-center sm:gap-4"
          >
            {isFilled.link(primary_cta) && (
              <PrismicNextLink
                field={primary_cta}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-[#00f557] px-7 py-3.5 text-[16px] font-bold leading-[1.5] text-[#002726] transition-opacity hover:opacity-90"
              />
            )}
            {isFilled.link(secondary_cta) && (
              <PrismicNextLink
                field={secondary_cta}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-full border border-white bg-transparent px-7 py-3.5 text-[16px] font-bold leading-[1.5] text-white transition-colors hover:bg-white hover:text-[#041118]"
              />
            )}
          </RevealOnScroll>
        )}
      </div>
    </section>
  );
};

/**
 * Proof to Scale variation — dark themed centered section: eyebrow pill,
 * large headline, description, and a single row of icon+title cards
 * separated by fading purple vertical dividers. Optional background image.
 */
type ProofToScaleSlice = {
  slice_type: string;
  variation: string;
  primary: {
    eyebrow?: string;
    headline?: import('@prismicio/client').RichTextField;
    description?: import('@prismicio/client').RichTextField;
    features?: {
      icon?: import('@prismicio/client').ImageField;
      title?: string;
    }[];
    background_image?: import('@prismicio/client').ImageField;
  };
};

const ProofToScale: FC<{ slice: ProofToScaleSlice }> = ({ slice }) => {
  const {
    eyebrow,
    headline,
    description,
    features = [],
    background_image,
  } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative mx-4 overflow-hidden rounded-[24px] bg-[#01060a] px-4 py-16 sm:mx-[30px] md:px-12 md:py-24"
    >
      {isFilled.image(background_image) && (
        <PrismicNextImage
          field={background_image}
          fill
          className="pointer-events-none absolute inset-0 size-full object-cover opacity-60"
          alt=""
        />
      )}

      <div className="relative z-[1] mx-auto flex w-full max-w-[1180px] flex-col items-center gap-[40px]">
        <div className="flex flex-col items-center gap-[20px] text-center">
          {eyebrow && (
            <span className="inline-block rounded-full border border-[#00f557] px-[16px] py-[8px] text-[14px] font-normal uppercase leading-[1.2] text-white">
              {eyebrow}
            </span>
          )}

          {isFilled.richText(headline) && (
            <PrismicRichText
              field={headline}
              components={{
                heading2: ({ children }) => (
                  <h2 className="max-w-[920px] text-[32px] font-bold leading-[1.2] text-white lg:text-[44px]">
                    {children}
                  </h2>
                ),
              }}
            />
          )}

          {isFilled.richText(description) && (
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="max-w-[820px] text-[14px] font-normal leading-[1.5] text-white/80 md:text-[16px]">
                    {children}
                  </p>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold">{children}</strong>
                ),
              }}
            />
          )}
        </div>

        {features.length > 0 && (
          <div className="relative w-full">
            <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 md:grid-cols-3 md:gap-0">
              {features.map((feature, i) => (
                <div
                  key={i}
                  className="flex flex-col items-center justify-start gap-[20px] px-6 text-center md:px-10"
                >
                  {isFilled.image(feature.icon) && (
                    <PrismicNextImage
                      field={feature.icon}
                      className="size-[64px] object-contain"
                      fallbackAlt=""
                    />
                  )}
                  {feature.title && (
                    <p className="max-w-[280px] text-[18px] font-bold leading-[1.3] text-white md:text-[20px]">
                      {feature.title}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Fading purple vertical dividers between columns */}
            {Array.from(
              { length: Math.max(features.length - 1, 0) },
              (_, i) => (
                <div
                  key={`v-${i}`}
                  className="pointer-events-none absolute inset-y-0 hidden w-px md:block"
                  style={{
                    left: `calc(100% / ${features.length} * ${i + 1})`,
                    background:
                      'linear-gradient(to bottom, transparent 0%, #a887ff 15%, #a887ff 85%, transparent 100%)',
                  }}
                />
              ),
            )}
          </div>
        )}
      </div>
    </section>
  );
};

/**
 * Diagnose Path variation — light themed centered section: purple eyebrow
 * pill, large bold headline, single row of chips separated by arrow icons
 * (custom or default '▸'), and a single green CTA button below. Optional
 * background image.
 */
type DiagnosePathSlice = {
  slice_type: string;
  variation: string;
  primary: {
    eyebrow?: string;
    headline?: import('@prismicio/client').RichTextField;
    chips?: { label?: string }[];
    arrow_icon?: import('@prismicio/client').ImageField;
    cta_link?: import('@prismicio/client').LinkField;
    background_image?: import('@prismicio/client').ImageField;
  };
};

const DiagnosePath: FC<{ slice: DiagnosePathSlice }> = ({ slice }) => {
  const {
    eyebrow,
    headline,
    chips = [],
    arrow_icon,
    cta_link,
    background_image,
  } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative overflow-hidden bg-white px-4 py-16 md:px-12 md:py-24"
    >
      {isFilled.image(background_image) && (
        <PrismicNextImage
          field={background_image}
          fill
          className="pointer-events-none absolute inset-0 size-full object-cover opacity-60"
          alt=""
        />
      )}

      <div className="relative z-[1] mx-auto flex w-full max-w-[1180px] flex-col items-center gap-[36px]">
        {eyebrow && (
          <span className="inline-block rounded-full border border-[#4b0fea] px-[18px] py-[8px] text-[12px] font-normal uppercase leading-[1.2] tracking-[0.12em] text-[#041118] md:text-[13px]">
            {eyebrow}
          </span>
        )}

        {isFilled.richText(headline) && (
          <PrismicRichText
            field={headline}
            components={{
              heading2: ({ children }) => {
                const flatten = (node: ReactNode): string => {
                  if (node == null) {
                    return '';
                  }
                  if (typeof node === 'string' || typeof node === 'number') {
                    return String(node);
                  }
                  if (Array.isArray(node)) {
                    return node.map(flatten).join('');
                  }
                  if (isValidElement<{ children?: ReactNode }>(node)) {
                    return flatten(node.props?.children);
                  }
                  return '';
                };
                const text = flatten(children);
                const pivot = text.indexOf(' Your ');
                if (pivot >= 0) {
                  return (
                    <h2 className="text-center text-[32px] font-bold leading-[1.2] text-[#041118] md:text-[44px]">
                      {text.slice(0, pivot)}
                      <br />
                      {text.slice(pivot + 1)}
                    </h2>
                  );
                }
                return (
                  <h2 className="max-w-[1080px] text-balance text-center text-[32px] font-bold leading-[1.2] text-[#041118] md:text-[44px]">
                    {children}
                  </h2>
                );
              },
            }}
          />
        )}

        {chips.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-x-[10px] gap-y-[12px]">
            {chips.map((chip, i) => (
              <Fragment key={`chip-${i}`}>
                {chip.label && (
                  <span className="inline-block rounded-full border border-[#041118]/30 bg-white px-[22px] py-[6px] text-[12px] font-normal leading-[1.4] text-[#041118] md:px-[26px] md:py-[7px] md:text-[13px]">
                    {chip.label}
                  </span>
                )}
                {i < chips.length - 1 && (
                  <span
                    aria-hidden="true"
                    className="inline-flex shrink-0 items-center text-[#041118]/55"
                  >
                    {isFilled.image(arrow_icon) ? (
                      <PrismicNextImage
                        field={arrow_icon}
                        className="h-[10px] w-auto object-contain"
                        alt=""
                      />
                    ) : (
                      <span className="text-[12px] leading-none">▸</span>
                    )}
                  </span>
                )}
              </Fragment>
            ))}
          </div>
        )}

        {isFilled.link(cta_link) && (
          <PrismicNextLink
            field={cta_link}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-full bg-[#00f557] px-7 py-3.5 text-[16px] font-bold leading-[1.5] text-[#002726] transition-opacity hover:opacity-90"
          />
        )}
      </div>
    </section>
  );
};

/**
 * Animated stat counter — eases from 0 to `target` once the element enters
 * the viewport. Honors prefers-reduced-motion.
 */
const StatCounter: FC<{
  target: number;
  suffix?: string;
  className?: string;
}> = ({ target, suffix = '', className }) => {
  // M-05: render the real value in markup; count up only as enhancement.
  const { display, ref } = useCountUp<HTMLDivElement>(target);

  return (
    <div ref={ref} className={className}>
      {display}
      {suffix}
    </div>
  );
};

/**
 * Proven Outcomes variation — light themed two-section: "Proven Outcomes"
 * (title + intro + animated 0→target stat counters in purple gradient,
 * separated by fading purple vertical lines + closing line) and "Why
 * Enterprises Trust Us" (title + bordered card row with bold title +
 * subtitle in parens). Optional background image.
 */
type ProvenOutcomesSlice = {
  slice_type: string;
  variation: string;
  primary: {
    outcomes_title?: string;
    outcomes_intro?: string;
    stats?: { value?: number; suffix?: string; label?: string }[];
    outcomes_closing?: string;
    trust_title?: string;
    trust_cards?: { title?: string; subtitle?: string }[];
    background_image?: import('@prismicio/client').ImageField;
  };
};

const ProvenOutcomes: FC<{ slice: ProvenOutcomesSlice }> = ({ slice }) => {
  const {
    outcomes_title,
    outcomes_intro,
    stats = [],
    outcomes_closing,
    trust_title,
    trust_cards = [],
    background_image,
  } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative overflow-hidden bg-white px-4 py-16 md:px-12 md:py-24"
    >
      {isFilled.image(background_image) && (
        <PrismicNextImage
          field={background_image}
          fill
          className="pointer-events-none absolute inset-0 size-full object-cover opacity-60"
          alt=""
        />
      )}

      <div className="relative z-[1] mx-auto flex w-full max-w-[1180px] flex-col items-center gap-[60px]">
        {(outcomes_title ||
          outcomes_intro ||
          stats.length > 0 ||
          outcomes_closing) && (
          <RevealOnScroll className="flex w-full flex-col items-center gap-[28px]">
            {outcomes_title && (
              <p className="text-center text-[20px] font-bold leading-[1.3] text-[#041118] md:text-[22px]">
                {outcomes_title}
              </p>
            )}
            {outcomes_intro && (
              <p className="max-w-[820px] text-center text-[14px] leading-[1.5] text-[#041118]/75 md:text-[16px]">
                {outcomes_intro}
              </p>
            )}
            {stats.length > 0 && (
              <div className="relative w-full">
                <div
                  className="grid w-full gap-10 md:gap-0"
                  style={{
                    gridTemplateColumns: `repeat(${stats.length}, minmax(0, 1fr))`,
                  }}
                >
                  {stats.map((stat, i) => (
                    <div
                      key={`stat-${i}`}
                      className="flex flex-col items-center gap-[12px] px-4 py-2 text-center md:px-6"
                    >
                      <div className="bg-gradient-to-b from-[#4b0fea] to-[#a887ff] bg-clip-text text-[52px] font-medium leading-[1] text-transparent md:text-[64px]">
                        <StatCounter
                          target={Number(stat.value ?? 0)}
                          suffix={stat.suffix || '%'}
                        />
                      </div>
                      {stat.label && (
                        <p className="max-w-[220px] text-[16px] font-bold leading-[1.3] text-[#041118] md:text-[18px]">
                          {stat.label}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                {Array.from({ length: stats.length - 1 }, (_, i) => (
                  <div
                    key={`stat-v-${i}`}
                    className="pointer-events-none absolute inset-y-0 hidden w-px bg-[#cccccc] md:block"
                    style={{
                      left: `calc(100% / ${stats.length} * ${i + 1})`,
                    }}
                  />
                ))}
              </div>
            )}
            {outcomes_closing && (
              <p className="max-w-[820px] text-center text-[14px] leading-[1.5] text-[#041118]/75 md:text-[16px]">
                {outcomes_closing}
              </p>
            )}
          </RevealOnScroll>
        )}

        {(trust_title || trust_cards.length > 0) && (
          <RevealOnScroll
            delay={150}
            className="flex w-full flex-col items-center gap-[24px] md:gap-[32px]"
          >
            {trust_title && (
              <p className="text-center text-[20px] font-bold leading-[1.3] text-[#041118] md:text-[22px]">
                {trust_title}
              </p>
            )}
            {trust_cards.length > 0 && (
              <div
                className={`grid w-full gap-y-6 md:gap-y-0 ${
                  trust_cards.length === 4
                    ? 'grid-cols-2 md:grid-cols-4'
                    : trust_cards.length === 3
                      ? 'grid-cols-1 sm:grid-cols-3'
                      : trust_cards.length === 2
                        ? 'grid-cols-2'
                        : trust_cards.length >= 5
                          ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-5'
                          : 'grid-cols-1'
                }`}
              >
                {trust_cards.map((card, i) => (
                  <RevealOnScroll
                    key={`trust-${i}`}
                    delay={200 + i * 100}
                    className={`flex flex-col items-center justify-start gap-[6px] px-4 py-[12px] text-center md:border-r md:border-solid md:border-[#cccccc] ${
                      i === 0 ? 'md:border-l' : ''
                    }`}
                  >
                    {card.title && (
                      <p className="text-[14px] font-bold leading-[1.3] text-[#041118] md:text-[15px]">
                        {card.title}
                      </p>
                    )}
                    {card.subtitle && (
                      <p className="text-[12px] font-normal leading-[1.4] text-[#041118]/65 md:text-[13px]">
                        {card.subtitle}
                      </p>
                    )}
                  </RevealOnScroll>
                ))}
              </div>
            )}
          </RevealOnScroll>
        )}
      </div>
    </section>
  );
};

/**
 * Industry Focus variation — dark themed centered section with eyebrow,
 * title, description, and two sub-sections (each with a green title and a
 * row of icon+title cards). Cards auto-fit the row.
 */
type IndustryFocusSlice = {
  slice_type: string;
  variation: string;
  primary: {
    eyebrow?: string;
    title?: import('@prismicio/client').RichTextField;
    description?: import('@prismicio/client').RichTextField;
    section_1_title?: string;
    section_1_cards?: {
      icon?: import('@prismicio/client').ImageField;
      title?: string;
    }[];
    section_2_title?: string;
    section_2_cards?: {
      icon?: import('@prismicio/client').ImageField;
      title?: string;
    }[];
    background_image?: import('@prismicio/client').ImageField;
  };
};

const IndustryFocus: FC<{ slice: IndustryFocusSlice }> = ({ slice }) => {
  const {
    eyebrow,
    title,
    description,
    section_1_title,
    section_1_cards = [],
    section_2_title,
    section_2_cards = [],
    background_image,
  } = slice.primary;

  const renderCardRow = (
    cards: { icon?: import('@prismicio/client').ImageField; title?: string }[],
    keyPrefix: string,
  ) => {
    if (cards.length === 0) {
      return null;
    }
    const cols = Math.min(cards.length, 6);
    return (
      <div className="relative w-full overflow-hidden">
        <div
          className="grid w-full grid-cols-2 gap-2 sm:grid-cols-3 lg:gap-0 lg:[grid-template-columns:repeat(var(--cols),minmax(0,1fr))]"
          style={{ '--cols': cols } as CSSProperties}
        >
          {cards.map((card, i) => (
            <div
              key={`${keyPrefix}-${i}`}
              className="group/icard relative flex min-h-[200px] flex-col items-center justify-center gap-[10px] overflow-hidden px-[24px] pb-[24px] pt-[33px] text-center backdrop-blur-[5.4px] transition-colors duration-300"
              style={{
                background:
                  'linear-gradient(-35.9771deg, rgba(19, 9, 74, 0.8) 26.508%, rgba(4, 17, 24, 0.8) 48.469%)',
              }}
            >
              {/* Hover wash — brightens the card and adds a soft purple
                  glow rising from the bottom. */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover/icard:opacity-100"
                style={{
                  background:
                    'radial-gradient(80% 70% at 50% 100%, rgba(168,135,255,0.30) 0%, rgba(168,135,255,0) 65%), linear-gradient(0deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.06) 100%)',
                }}
              />
              {isFilled.image(card.icon) && (
                <PrismicNextImage
                  field={card.icon}
                  className="relative size-[60px] object-contain transition-transform duration-300 group-hover/icard:scale-110 md:size-[75px]"
                  fallbackAlt=""
                />
              )}
              {card.title && (
                <p className="relative max-w-[250px] text-balance text-[18px] font-semibold leading-[1.5] text-white md:text-[24px]">
                  {card.title}
                </p>
              )}
            </div>
          ))}
        </div>
        {/* Purple vertical dividers between cards — transparent in the
            top half, fade in around the middle (50%) and solid down to
            the bottom edge. */}
        {Array.from({ length: cols - 1 }, (_, i) => (
          <div
            key={`${keyPrefix}-divider-${i}`}
            className="pointer-events-none absolute inset-y-0 hidden w-px lg:block"
            style={{
              left: `calc(100% / ${cols} * ${i + 1})`,
              background:
                'linear-gradient(to bottom, transparent 0%, transparent 45%, #a887ff 55%, #a887ff 100%)',
            }}
          />
        ))}
        {/* Horizontal purple divider along the bottom of the card row —
            same fade pattern as the vertical dividers. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 hidden h-px lg:block"
          style={{
            background:
              'linear-gradient(to right, transparent 0%, #a887ff 30%, #a887ff 70%, transparent 100%)',
          }}
        />
      </div>
    );
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative mx-4 overflow-hidden rounded-[24px] bg-[#01060a] px-4 py-16 sm:mx-[30px] md:px-12 md:py-24"
    >
      {isFilled.image(background_image) && (
        <PrismicNextImage
          field={background_image}
          fill
          className="pointer-events-none absolute inset-0 size-full object-cover opacity-50"
          alt=""
        />
      )}

      <div className="relative z-[1] mx-auto flex w-full max-w-[1180px] flex-col items-center gap-[40px]">
        <RevealOnScroll className="flex flex-col items-center gap-[16px] text-center">
          {eyebrow && (
            <span className="inline-block rounded-full border border-[#00f557] px-[16px] py-[8px] text-[14px] font-normal uppercase leading-[1.2] text-white">
              {eyebrow}
            </span>
          )}

          {isFilled.richText(title) && (
            <PrismicRichText
              field={title}
              components={{
                heading2: ({ children }) => (
                  <h2 className="w-full text-balance text-[32px] font-semibold leading-[1.2] text-white lg:whitespace-nowrap lg:text-[44px]">
                    {children}
                  </h2>
                ),
              }}
            />
          )}

          {isFilled.richText(description) && (
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="w-full text-pretty text-[14px] font-normal leading-[1.5] text-white/80 lg:whitespace-nowrap md:text-[16px]">
                    {children}
                  </p>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold">{children}</strong>
                ),
              }}
            />
          )}
        </RevealOnScroll>

        {(section_1_title || section_1_cards.length > 0) && (
          <RevealOnScroll
            delay={150}
            className="flex w-full flex-col items-center gap-[24px]"
          >
            {section_1_title && (
              <p className="text-center text-[22px] font-semibold leading-[1.4] tracking-normal text-[#00f557]">
                {section_1_title}
              </p>
            )}
            {renderCardRow(section_1_cards, 'sec1')}
          </RevealOnScroll>
        )}

        {(section_2_title || section_2_cards.length > 0) && (
          <RevealOnScroll
            delay={250}
            className="flex w-full flex-col items-center gap-[24px]"
          >
            {section_2_title && (
              <p className="text-center text-[22px] font-semibold leading-[1.4] tracking-normal text-[#00f557]">
                {section_2_title}
              </p>
            )}
            {renderCardRow(section_2_cards, 'sec2')}
          </RevealOnScroll>
        )}
      </div>
    </section>
  );
};

/**
 * Component for "FeatureOverview2" Slices.
 */
const FeatureOverview2: FC<FeatureOverview2Props> = ({ slice }) => {
  if (slice.variation === 'tableVersion') {
    return <TableVersion slice={slice} />;
  }
  if (slice.variation === 'pathForward') {
    return <PathForward slice={slice} />;
  }
  if ((slice.variation as string) === 'pilotBuild') {
    return <PilotBuild slice={slice as unknown as PilotBuildSlice} />;
  }
  if ((slice.variation as string) === 'complexityCost') {
    return <ComplexityCost slice={slice as unknown as ComplexityCostSlice} />;
  }
  if ((slice.variation as string) === 'structuredDelivery') {
    return (
      <StructuredDelivery slice={slice as unknown as StructuredDeliverySlice} />
    );
  }
  if ((slice.variation as string) === 'proofToScale') {
    return <ProofToScale slice={slice as unknown as ProofToScaleSlice} />;
  }
  if ((slice.variation as string) === 'diagnosePath') {
    return <DiagnosePath slice={slice as unknown as DiagnosePathSlice} />;
  }
  if ((slice.variation as string) === 'provenOutcomes') {
    return <ProvenOutcomes slice={slice as unknown as ProvenOutcomesSlice} />;
  }
  if ((slice.variation as string) === 'industryFocus') {
    return <IndustryFocus slice={slice as unknown as IndustryFocusSlice} />;
  }
  return (
    <DefaultGrid
      slice={
        slice as Extract<
          Content.FeatureOverview2Slice,
          { variation: 'default_grid' }
        >
      }
    />
  );
};

export default FeatureOverview2;
